import { useCallback } from 'react';

import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { ShipmentEmbryo } from '@/services/shipping/types';
import { runNewWorkflow, WORKFLOW } from '@/utils/workflow';

export interface UseRemoveFromShipmentWorkflowOptions {
  selectedEmbryos: ShipmentEmbryo[];
  dispatch: any;
  onStart?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

export function useRemoveFromShipmentWorkflow({
  dispatch,
  onStart,
  onSuccess,
  onError,
}: Omit<UseRemoveFromShipmentWorkflowOptions, 'selectedEmbryos'>) {
  const confirm = useConfirm();
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');
  const runWorkflow = useCallback(
    async (embryos: ShipmentEmbryo[]) => {
      onStart?.();
      try {
        const recordIds = embryos.map((embryo) => ({ RecordId: embryo.id }));
        await runNewWorkflow(recordIds, WORKFLOW.RemoveFromShipment.id);
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Workflow Executed',
            detail: `${WORKFLOW.RemoveFromShipment.label} workflow completed successfully. You may need to refresh the data because workflow changes are not automatically reflected.`,
            life: ToastLife.LONG,
          }),
        );
        if (onSuccess) onSuccess();
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Workflow Error',
            detail: `Failed to execute ${WORKFLOW.RemoveFromShipment.label} workflow. Please try again. Error: ${error}`,
            life: ToastLife.LONG,
          }),
        );
        if (onError) onError();
      }
    },
    [dispatch, onStart, onSuccess, onError],
  );

  const executeWorkflow = useCallback(
    (embryos: ShipmentEmbryo[]) => {
      if (confirmWorkflows) {
        confirm({
          title: 'Confirm Workflow',
          description: (
            <>
              Run <b>{WORKFLOW.RemoveFromShipment.label}</b> workflow for{' '}
              {embryos.length} embryo{embryos.length === 1 ? '' : 's'}?
            </>
          ),
          confirmLabel: 'OK',
          cancelLabel: 'Cancel',
          onConfirm() {
            runWorkflow(embryos);
          },
        });
      } else {
        return runWorkflow(embryos);
      }
    },
    [confirm, confirmWorkflows, runWorkflow],
  );

  return { executeWorkflow };
}
